import { useCallback, useEffect, useState } from 'react';
import { Home, Save, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminUpdate } from '../../lib/adminApi';

interface JoepaKoppelingSectionProps {
  userId: string;
}

/**
 * Admin-blok voor de joepa-koppeling: de dibbi-blogs van deze gebruiker als
 * echte pagina's op zijn joepa-website (<domein>/blog/<slug>).
 *
 * De admin richt de koppeling in twee stappen in:
 * 1. In joepa's D1 een rij in `dibbi_koppelingen` (site + dibbi-user-id +
 *    widget-API-key) — buiten dibbi om.
 * 2. Hier de site-URL invullen en op actief zetten. Het aanzetten triggert
 *    database-side een 'resync'-ping naar joepa (eerste volledige render);
 *    daarna houden triggers op published_blogs alles automatisch bij.
 *
 * De gebruiker ziet de status terug op de joepa-tegel in Instellingen > Koppeling
 * en alle blog-URL's in dashboard/social/campagnes wijzen naar de joepa-site.
 */
export function JoepaKoppelingSection({ userId }: JoepaKoppelingSectionProps) {
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editUrl, setEditUrl] = useState('');
  const [editActive, setEditActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('users')
      .select('joepa_site_url, joepa_active')
      .eq('id', userId)
      .maybeSingle();
    setSiteUrl(data?.joepa_site_url ?? null);
    setActive(!!data?.joepa_active);
    setEditUrl(data?.joepa_site_url ?? '');
    setEditActive(!!data?.joepa_active);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = editActive !== active || (editUrl.trim() || null) !== (siteUrl || null);
  const blogUrl =
    active && siteUrl
      ? `${(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`).replace(/\/+$/, '')}/blog`
      : null;

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: userId,
        data: {
          joepa_site_url: editUrl.trim() || null,
          joepa_active: editActive,
        },
      });
      await load();
      setSuccess(
        editActive && !active
          ? 'joepa-koppeling geactiveerd; resync naar joepa is gestart'
          : 'joepa-koppeling opgeslagen',
      );
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Error saving joepa koppeling:', err);
      setError('Opslaan mislukt, probeer het opnieuw');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Home className="w-5 h-5 text-[#FF6B35]" />
        <h3 className="text-lg font-semibold text-gray-900">joepa-koppeling</h3>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${loading ? 'bg-gray-300 animate-pulse' : active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {loading ? 'Laden...' : active ? 'Actief' : 'Niet actief'}
        </span>
        {blogUrl && (
          <a
            href={blogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-[#FF6B35] hover:underline ml-1"
          >
            Bekijk blog
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Blogs van deze gebruiker als echte pagina&apos;s op zijn joepa-website. Eerst de koppeling
        in joepa aanmaken (dibbi-user-id + widget-API-key), dan hier de site-URL invullen en
        activeren; dat start automatisch de eerste volledige synchronisatie.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">joepa-site-URL</label>
          <input
            type="text"
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            placeholder="https://www.klantdomein.nl"
            disabled={loading}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35] disabled:bg-gray-50"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none pb-2">
          <input
            type="checkbox"
            checked={editActive}
            onChange={(e) => setEditActive(e.target.checked)}
            disabled={loading}
            className="w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35]"
          />
          <span className="text-sm font-medium text-gray-700">Koppeling actief</span>
        </label>
        <button
          onClick={handleSave}
          disabled={saving || loading || !dirty}
          className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e85a20] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>

      {success && <p className="text-sm text-emerald-600 mt-3">{success}</p>}
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
    </div>
  );
}
